/**
 * React Query hook for Event Types with Pagination Support
 * Designed for use with react-select-async-paginate
 * Provides automatic caching, background sync, and proper pagination handling
 */

import { useQuery } from '@tanstack/react-query';
import { listEventTypes } from 'services/Calender/calendarServices';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches event types with pagination tokens
 *
 * @param {Object} options - Query options
 * @param {number} options.pageSize - Number of items per page (default: 20)
 * @param {string} options.pagination_token - Token for pagination
 * @param {string} options.sort_order - Sort order (default: 'alphabet_ascending')
 * @param {string} options.searchQuery - Search query for filtering
 * @param {string|array} options.practiceAreaId - Practice area ID(s) to filter
 * @returns {Promise<Object>} - { options, hasMore, additional: { nextToken } }
 */
const fetchEventTypesPage = async ({
  pageSize = 20,
  pagination_token = '',
  sort_order = 'alphabet_ascending',
  searchQuery = '',
  practiceAreaId = '',
}) => {
  try {
    const response = await listEventTypes({
      pageLimit: pageSize,
      pagination_token,
      pagination_direction: 'forward',
      excludeFromLoading: true,
      practiceAreaId,
      sort_order,
    });

    const eventTypes = response?.data?.['event-type'] || [];
    const nextToken = response?.data?.next_token || null;

    // Filter by search query if provided
    const filteredTypes = searchQuery
      ? eventTypes.filter((et) => et.event_type?.toLowerCase().includes(searchQuery.toLowerCase()))
      : eventTypes;

    // Transform to react-select format
    const options = filteredTypes.map((et) => ({
      label: et.event_type,
      value: et.event_type_id,
      icon: et.event_type_icon,
      ...et, // Include full object for additional data access
    }));

    return {
      options,
      hasMore: nextToken !== null && nextToken !== undefined && nextToken !== '',
      additional: {
        nextToken: nextToken || '',
      },
    };
  } catch (error) {
    console.error('Error fetching event types page:', error);
    throw error;
  }
};

/**
 * Hook to fetch paginated event types
 * Compatible with react-select-async-paginate
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.searchQuery - Search query for filtering
 * @param {string} params.pagination_token - Token for current page
 * @param {number} params.pageSize - Number of items per page (default: 20)
 * @param {string} params.sort_order - Sort order (default: 'alphabet_ascending')
 * @param {string|array} params.practiceAreaId - Practice area ID(s) to filter
 * @param {boolean} params.enabled - Enable/disable query (default: true)
 * @returns {Object} - { data, isLoading, error, refetch }
 */
export const useEventTypesPaginated = ({
  searchQuery = '',
  pagination_token = '',
  pageSize = 20,
  sort_order = 'alphabet_ascending',
  practiceAreaId = '',
  enabled = true,
} = {}) => {
  const queryKey = queryKeys.eventTypes.paginated({
    searchQuery,
    pagination_token,
    pageSize,
    sort_order,
    practiceAreaId,
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      fetchEventTypesPage({
        pageSize,
        pagination_token,
        sort_order,
        searchQuery,
        practiceAreaId,
      }),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });

  return {
    data: data || { options: [], hasMore: false, additional: { nextToken: '' } },
    isLoading,
    error,
    refetch,
  };
};

/**
 * Load options function for react-select-async-paginate
 * This is a standalone function that can be used directly with AsyncPaginate
 *
 * @param {string} search - Search query
 * @param {Array} loadedOptions - Previously loaded options
 * @param {Object} additional - Additional data (pagination token)
 * @param {Object} config - Configuration options
 * @returns {Promise<Object>} - { options, hasMore, additional }
 */
export const loadEventTypeOptions = async (search, loadedOptions, additional, config = {}) => {
  const { pageSize = 20, sort_order = 'alphabet_ascending', practiceAreaId = '' } = config;

  try {
    const pagination_token = additional?.nextToken || '';

    const result = await fetchEventTypesPage({
      pageSize,
      pagination_token,
      sort_order,
      searchQuery: search,
      practiceAreaId,
    });

    return result;
  } catch (error) {
    console.error('Error loading event type options:', error);
    return {
      options: [],
      hasMore: false,
      additional: { nextToken: '' },
    };
  }
};
